import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import TutorialButton from './common/TutorialButton';
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { tratarErroAPI, logarRequisicao, logarSucesso } from '../utils/errorHandler';
import '../styles/municipio.css';
import etgLogo from '../assets/etg.png';
import ufmgLogo from '../assets/Ufmg-sem nome-03.png';
import '../styles/home.css';

const MunicipioManager = () => {
  const navigate = useNavigate();
  const [dados, setDados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [formData, setFormData] = useState({
    id_municipio: '',
    nome_municipio: '',
    id_sigla_est: '',
    id_superintendencia: '',
    geom_limite: '',
    geom_limite_urbano: ''
  });
  const [apiStatus, setApiStatus] = useState('checking');
  const [lastUpdate, setLastUpdate] = useState(null);
  const [estados, setEstados] = useState([]);
  const [superintendencias, setSuperintendencias] = useState([]);
  const [camposComErro, setCamposComErro] = useState([]);

  const tabulatorRef = useRef(null);
  const tableRef = useRef(null);

  const mostrarMensagem = useCallback((tipo, texto) => {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem({ tipo: '', texto: '' }), 4000);
  }, []);

  const fetchEstados = useCallback(async () => {
    try {
      const response = await api.get('/estado');
      setEstados(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar estados: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const fetchSuperintendencias = useCallback(async () => {
    try {
      const response = await api.get('/superintendencia');
      setSuperintendencias(response.data.data || []);
    } catch (error) {
      mostrarMensagem('error', `Erro ao carregar superintendências: ${error.message}`);
    }
  }, [mostrarMensagem]);

  const verificarAPI = useCallback(async () => {
    try {
      setApiStatus('checking');
      await api.get('/municipio');
      setApiStatus('connected');
    } catch (error) {
      setApiStatus('error');
      if (error.response && error.response.status === 401) {
        mostrarMensagem('error', 'Erro de autenticação. Faça login novamente.');
      } else {
        mostrarMensagem('error', `Erro de conexão: ${error.message}`);
      }
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    verificarAPI();
    fetchEstados();
    fetchSuperintendencias();
  }, [verificarAPI, fetchEstados, fetchSuperintendencias]);

  const atualizarRegistro = useCallback(async (id, dadosAtualizacao) => {
    logarRequisicao('Atualizar', 'município', { id, dadosAtualizacao });

    try {
      const response = await api.put(`/municipio/${id}`, dadosAtualizacao);
      logarSucesso('Atualizar', 'município', response.data);
      if (response && response.data) {
        if (tabulatorRef.current) {
          tabulatorRef.current.updateData([response.data.data]);
        }
        mostrarMensagem('success', 'Registro atualizado com sucesso!');
      } else {
        mostrarMensagem('error', 'Resposta da API inválida ao atualizar.');
      }
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'atualizar', 'município');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const excluirRegistro = useCallback(async (id) => {
    logarRequisicao('Excluir', 'município', { id });

    try {
      const response = await api.delete(`/municipio/${id}`);
      logarSucesso('Excluir', 'município', response.data);
      setDados(currentData => currentData.filter(item => item.id_municipio !== id));
      mostrarMensagem('success', 'Registro excluído com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'excluir', 'município');
      mostrarMensagem('error', mensagemErro);
    }
  }, [mostrarMensagem]);

  const colunas = useMemo(() => {
    const estadosNomes = estados.reduce((acc, estado) => {
      acc[estado.id_sigla_est] = estado.id_sigla_est;
      return acc;
    }, {});

    const superintendenciasNomes = superintendencias.reduce((acc, sup) => {
      acc[sup.id_superintendencia] = sup.nome_super;
      return acc;
    }, {});

    return [
      {
        title: 'Salvar',
        field: 'salvar',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-save"><i class="fas fa-save"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          atualizarRegistro(data.id_municipio, data);
        },
        headerSort: false
      },
      {
        title: 'Excluir',
        field: 'excluir',
        width: 80,
        hozAlign: 'center',
        formatter: () => '<button class="btn-delete"><i class="fas fa-trash"></i></button>',
        cellClick: (e, cell) => {
          const data = cell.getRow().getData();
          if (window.confirm(`Deseja realmente excluir o registro "${data.id_municipio}"?`)) {
            excluirRegistro(data.id_municipio);
          }
        },
        headerSort: false
      },
      {
        title: 'ID',
        field: 'id_municipio',
        width: 200,
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar ID...',
        sorter: 'string'
      },
      {
        title: 'Nome do Município',
        field: 'nome_municipio',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar nome...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Sigla Estado',
        field: 'id_sigla_est',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar sigla...',
        sorter: 'string',
        formatter: (cell) => {
          return estadosNomes[cell.getValue()] || cell.getValue();
        },
        editor: 'list',
        editorParams: {
          values: estadosNomes
        }
      },
      {
        title: 'Superintendência',
        field: 'id_superintendencia',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar super...',
        sorter: 'string',
        formatter: (cell) => {
          return superintendenciasNomes[cell.getValue()] || cell.getValue();
        },
        editor: 'list',
        editorParams: {
          values: superintendenciasNomes
        }
      },
      {
        title: 'Geom Limite',
        field: 'geom_limite',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar geom limite...',
        sorter: 'string',
        editor: 'input'
      },
      {
        title: 'Geom Limite Urbano',
        field: 'geom_limite_urbano',
        headerFilter: 'input',
        headerFilterPlaceholder: 'Filtrar geom limite urbano...',
        sorter: 'string',
        editor: 'input'
      }
    ];
  }, [estados, superintendencias, atualizarRegistro, excluirRegistro]);

  const carregarDados = useCallback(async (showMessage = true) => {
    try {
      setLoading(true);
      const response = await api.get('/municipio');
      setDados(response.data.data || []);
      setLastUpdate(new Date());
      if (showMessage) {
        mostrarMensagem('success', `${response.data.data?.length || 0} registros carregados`);
      }
    } catch (error) {
      mostrarMensagem('error', error.message);
      setDados([]);
    } finally {
      setLoading(false);
    }
  }, [mostrarMensagem]);

  useEffect(() => {
    if (apiStatus === 'connected') {
      carregarDados(false);
    }
  }, [apiStatus, carregarDados]);

  const inicializarTabulator = useCallback(() => {
    if (!tableRef.current) return;
    if (tabulatorRef.current) tabulatorRef.current.destroy();

    tabulatorRef.current = new Tabulator(tableRef.current, {
      data: dados,
      columns: colunas,
      layout: 'fitData',
      pagination: 'local',
      paginationSize: 10,
      paginationCounter: 'rows',
      movableColumns: true,
      resizableRows: true,
      tooltips: true,
      placeholder: 'Nenhum registro encontrado',
      index: 'id_municipio'
    });

    const style = document.createElement('style');
    style.textContent = `
      .btn-save {
        background: #007bff;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-save:hover {
        background: #0056b3;
      }
      .btn-delete {
        background: #dc3545;
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 3px;
        cursor: pointer;
        font-size: 12px;
      }
      .btn-delete:hover {
        background: #c82333;
      }
      .btn-back {
        background: transparent;
        border: none;
        font-size: 24px;
        cursor: pointer;
      }
    `;
    document.head.appendChild(style);
  }, [dados, colunas]);

  useEffect(() => {
    if (apiStatus === 'connected' && tableRef.current) {
      if (!tabulatorRef.current) {
        inicializarTabulator();
      } else {
        tabulatorRef.current.setColumns(colunas);
        tabulatorRef.current.setData(dados);
      }
    }
  }, [dados, apiStatus, inicializarTabulator, colunas]);

  const adicionarRegistro = async (e) => {
    e.preventDefault();
    const { id_municipio, nome_municipio, id_sigla_est, id_superintendencia, geom_limite, geom_limite_urbano } = formData;

    // Validação de campos obrigatórios com mensagens específicas
    const camposObrigatorios = [];
    const erros = [];

    if (!id_municipio) {
      camposObrigatorios.push('ID');
      erros.push('id_municipio');
    }
    if (!nome_municipio) {
      camposObrigatorios.push('Nome do Município');
      erros.push('nome_municipio');
    }
    if (!id_sigla_est) {
      camposObrigatorios.push('Sigla Estado');
      erros.push('id_sigla_est');
    }
    if (!id_superintendencia) {
      camposObrigatorios.push('Superintendência');
      erros.push('id_superintendencia');
    }

    if (camposObrigatorios.length > 0) {
      setCamposComErro(erros);
      mostrarMensagem('error', `Por favor, preencha os campos obrigatórios: ${camposObrigatorios.join(', ')}`);
      return;
    }

    setCamposComErro([]);

    const dataToSend = {
      id_municipio,
      nome_municipio,
      id_sigla_est,
      id_superintendencia,
      geom_limite: geom_limite === '' ? null : geom_limite,
      geom_limite_urbano: geom_limite_urbano === '' ? null : geom_limite_urbano
    };

    logarRequisicao('Adicionar', 'município', dataToSend);

    try {
      setLoading(true);
      const response = await api.post('/municipio', dataToSend);
      logarSucesso('Adicionar', 'município', response.data);
      setDados([...dados, response.data.data]);
      setFormData({ id_municipio: '', nome_municipio: '', id_sigla_est: '', id_superintendencia: '', geom_limite: '', geom_limite_urbano: '' });
      setCamposComErro([]);
      mostrarMensagem('success', 'Registro adicionado com sucesso!');
    } catch (error) {
      const mensagemErro = tratarErroAPI(error, 'adicionar', 'município');
      mostrarMensagem('error', mensagemErro);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));

    // Remove o erro do campo quando ele for preenchido
    if (value || type === 'checkbox') {
      setCamposComErro(prev => prev.filter(campo => campo !== name));
    }
  };

  const exportarDados = () => {
    if (tabulatorRef.current) {
      tabulatorRef.current.download('csv', 'municipios.csv');
    }
  };

  return (
    <div className="tipo-ambiente-container">
      <div className="header-controls">
        <button className="btn-back" onClick={() => navigate('/')}><i className="fas fa-arrow-left"></i></button>
      </div>

      <div className="header">
        <div className="title-container">
          <img src={etgLogo} alt="ETG Logo" className="logo" />
          <h1>Gerenciador de Municípios</h1>
          <img src={ufmgLogo} alt="UFMG Logo" className="logo" />
        </div>
        <p>Interface para popular e gerenciar a tabela tb_municipio</p>
        <div style={{ marginTop: '10px' }}>
          <TutorialButton videoPath="bdados/municipio.mov" buttonText="Ver Tutorial Município" />
        </div>
      </div>

      {mensagem.texto && (
        <div className="status-container">
          <div className={`status ${mensagem.tipo}`}>{mensagem.texto}</div>
        </div>
      )}

      <div className="controls">
        <form onSubmit={adicionarRegistro} className="form-inline">
          <div className="form-group">
            <label htmlFor="id_municipio">ID:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="number"
              id="id_municipio"
              name="id_municipio"
              value={formData.id_municipio}
              onChange={handleInputChange}
              placeholder="Ex: 290010"
              maxLength={7}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_municipio') ? 'input-error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="nome_municipio">Nome do município:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <input
              type="text"
              id="nome_municipio"
              name="nome_municipio"
              value={formData.nome_municipio}
              onChange={handleInputChange}
              placeholder="Ex: Salvador"
              maxLength={100}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('nome_municipio') ? 'input-error' : ''}
            />
          </div>
          <div className="form-group">
            <label htmlFor="id_sigla_est">Sigla estado:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select
              id="id_sigla_est"
              name="id_sigla_est"
              value={formData.id_sigla_est}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_sigla_est') ? 'input-error' : ''}
            >
              <option value="">Selecione um estado</option>
              {estados.map(estado => (
                <option key={estado.id_sigla_est} value={estado.id_sigla_est}>{estado.id_sigla_est}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="id_superintendencia">Superintendência:<span style={{ color: 'red', marginLeft: '2px' }}>*</span></label>
            <select
              id="id_superintendencia"
              name="id_superintendencia"
              value={formData.id_superintendencia}
              onChange={handleInputChange}
              disabled={loading || apiStatus !== 'connected'}
              className={camposComErro.includes('id_superintendencia') ? 'input-error' : ''}
            >
              <option value="">Selecione uma superintendência</option>
              {superintendencias.map(sup => (
                <option key={sup.id_superintendencia} value={sup.id_superintendencia}>{sup.nome_super}</option>
              ))}
            </select>
          </div>
          <div className="form-actions-row">
            <div className="form-buttons">
              <button type="submit" className="btn btn-success" disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Adicionando...' : '➕ Adicionar'}
              </button>
              <button className="btn btn-primary" onClick={() => carregarDados(true)} disabled={loading || apiStatus !== 'connected'}>
                {loading ? '⏳ Carregando...' : '🔄 Recarregar'}
              </button>
              <button className="btn btn-secondary" onClick={exportarDados} disabled={dados.length === 0}>📥 Exportar CSV</button>
            </div>

            <div className="info-campos-obrigatorios">
              <i className="fas fa-info-circle"></i>
              <span>* indica que os campos são obrigatorios.</span>
            </div>
          </div>
        </form>
      </div>

      <div className="stats-bar">
        <div className="stats-item"><strong>{dados.length}</strong> registros carregados</div>
        <div className="stats-item">Última atualização: <strong>{lastUpdate ? lastUpdate.toLocaleTimeString() : 'N/A'}</strong></div>
      </div>

      <div className="tabulator-container">
        <div ref={tableRef}></div>
      </div>

    </div>
  );
};

export default MunicipioManager;